import React, { useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import type {
  ImportedMedicationLineResponse,
  MarMedicationResponse,
  PatientMedicationListItem,
  PatientMedicationResponse,
} from '../../services/api';
import MedicationTableRow from './MedicationTableRow';
import ImportedMedicationRow from '../imported/ImportedMedicationRow';
import {
  getMedicationDisplayName,
  isActuallyDiscontinued,
} from './medicationUtils';
import {
  getMedicationStatusFlags,
  MEDICATION_STATUS_FLAG_LEGEND,
} from './medicationStatusFlags';
import { parseISOToLocalDate } from '../../utils/dateUtils';
import {
  MAR_NO_VISIT_REASON,
  MAR_VISIT_NOT_EDITABLE_REASON,
} from './mar/messages';
import { useIsMobile } from '../../hooks/useIsMobile';

/** Epoch ms for a date-only string, parsed in local time (0 when absent/invalid). */
const dateMs = (value?: string | null): number =>
  parseISOToLocalDate(value ?? undefined)?.getTime() ?? 0;

export interface MedicationSelectionState {
  selectedIds: Set<string>;
  getEligibility: (medication: PatientMedicationResponse) => {
    eligible: boolean;
    reason?: string;
  };
  onToggle: (medicationId: string, checked: boolean) => void;
}

export interface MedicationMarContext {
  /** Selected visit; when set, administration affordances are shown. */
  visitId?: string;
  /** Whether the selected visit is still editable (open, not signed/completed). */
  visitEditable: boolean;
  marByMedicationId: Map<string, MarMedicationResponse[]>;
  onAddMar: (medication: PatientMedicationResponse) => void;
  onEditMar: (entry: MarMedicationResponse) => void;
  onDeleteMar: (entry: MarMedicationResponse) => void;
}

interface MedicationsTableProps {
  medications: PatientMedicationListItem[];
  onEdit: (medication: PatientMedicationResponse) => void;
  onDelete: (medication: PatientMedicationResponse) => void;
  onDiscontinue: (medicationId: string, date: Date) => Promise<void>;
  selection?: MedicationSelectionState;
  mar?: MedicationMarContext;
  /**
   * Raw Kinnser lines still awaiting a clinician's ruling (EMR-1369).
   *
   * A separate array, and separate on purpose: `medications` is the coded list
   * that CMS-485 prefill, order generation and interaction screening read, and
   * an uncoded line in it would be treated as clinical fact by all three. They
   * share a table because that is where a clinician needs to see them; they
   * never share a type.
   */
  importedLines?: ImportedMedicationLineResponse[];
}

type SortColumn = 'start_date' | 'discontinue_date' | 'medication';
type SortDirection = 'asc' | 'desc';

const MedicationsTable: React.FC<MedicationsTableProps> = ({
  medications,
  onEdit,
  onDelete,
  onDiscontinue,
  selection,
  mar,
  importedLines = [],
}) => {
  const isMobile = useIsMobile();
  const showMarColumn = Boolean(mar);
  // Recording is allowed only when a visit is selected AND still editable.
  const canRecordMar = Boolean(mar?.visitId) && Boolean(mar?.visitEditable);
  const marBlockedReason = !mar?.visitId
    ? MAR_NO_VISIT_REASON
    : MAR_VISIT_NOT_EDITABLE_REASON;
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSortClick = (column: SortColumn) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className='h-4 w-4 ml-1' />
    ) : (
      <ArrowDown className='h-4 w-4 ml-1' />
    );
  };

  const sortedMedications = [...medications].sort((a, b) => {
    if (!sortColumn) {
      const aDiscontinued = isActuallyDiscontinued(a);
      const bDiscontinued = isActuallyDiscontinued(b);
      if (aDiscontinued !== bDiscontinued) {
        return aDiscontinued ? 1 : -1;
      }
      return dateMs(b.start_date) - dateMs(a.start_date);
    }

    let comparison = 0;
    switch (sortColumn) {
      case 'start_date':
        comparison = dateMs(a.start_date) - dateMs(b.start_date);
        break;
      case 'discontinue_date':
        if (a.discontinue_date && b.discontinue_date) {
          comparison = dateMs(a.discontinue_date) - dateMs(b.discontinue_date);
        } else if (a.discontinue_date) {
          comparison = -1;
        } else if (b.discontinue_date) {
          comparison = 1;
        } else {
          comparison = 0;
        }
        break;
      case 'medication': {
        const aName = getMedicationDisplayName(a).toLowerCase();
        const bName = getMedicationDisplayName(b).toLowerCase();
        comparison = aName.localeCompare(bName);
        break;
      }
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // The Status column abbreviates the recency flags to LS / N / C, so something
  // on the page has to say what they mean. This does, once, for every row and
  // every viewport: a per-chip tooltip would be unreachable on a phone, where
  // Radix suppresses tooltips for touch pointers.
  //
  // Shown only when a row actually carries a flag, so a patient whose
  // medications have none does not read a key to an empty lock.
  const hasStatusFlags = medications.some(
    med => getMedicationStatusFlags(med).length > 0
  );
  const flagLegend = hasStatusFlags ? (
    <p
      data-testid='medication-status-flag-legend'
      className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'
    >
      {MEDICATION_STATUS_FLAG_LEGEND.map(flag => (
        <span key={flag.code}>
          <span className='font-semibold text-foreground'>{flag.code}</span>{' '}
          {flag.label}
        </span>
      ))}
    </p>
  ) : null;

  // Per-medication props shared by the desktop row and the mobile card so the
  // selection/MAR wiring stays identical across variants.
  const rowProps = (med: PatientMedicationListItem) => ({
    medication: med,
    onEdit,
    onDelete,
    onDiscontinue,
    selection: selection
      ? {
          selected: selection.selectedIds.has(med.id),
          ...selection.getEligibility(med),
          onToggle: (checked: boolean) => selection.onToggle(med.id, checked),
        }
      : undefined,
    mar: mar
      ? {
          entries: mar.marByMedicationId.get(med.id) ?? [],
          canRecord: canRecordMar,
          blockedReason: marBlockedReason,
          onAdd: () => mar.onAddMar(med),
          onEdit: mar.onEditMar,
          onDelete: mar.onDeleteMar,
        }
      : undefined,
  });

  // Below Tailwind's `md` breakpoint the fixed-width table overflows, so render
  // stacked cards instead of a horizontally scrolling table.
  if (isMobile) {
    const sortButtons: { column: SortColumn; label: string }[] = [
      { column: 'start_date', label: 'Start Date' },
      { column: 'discontinue_date', label: 'Discontinue' },
      { column: 'medication', label: 'Medication' },
    ];
    return (
      <div className='space-y-3'>
        <div
          className='flex flex-wrap items-center gap-2'
          role='group'
          aria-label='Sort medications'
        >
          {sortButtons.map(({ column, label }) => (
            <button
              key={column}
              type='button'
              className={`flex items-center rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                sortColumn === column
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => handleSortClick(column)}
              aria-pressed={sortColumn === column}
            >
              {label}
              {getSortIcon(column)}
            </button>
          ))}
        </div>
        {flagLegend}
        {sortedMedications.map(med => (
          <MedicationTableRow key={med.id} variant='card' {...rowProps(med)} />
        ))}
        {importedLines.map(line => (
          <ImportedMedicationRow key={line.id} line={line} variant='card' />
        ))}
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      {flagLegend}
      <Table>
        <TableHeader>
          <TableRow>
            {selection && <TableHead className='w-[44px]'></TableHead>}
            <TableHead className='w-[120px]'>Status</TableHead>
            <TableHead className='w-[130px]'>
              <button
                className='flex items-center hover:text-foreground transition-colors'
                onClick={() => handleSortClick('start_date')}
              >
                Start Date
                {getSortIcon('start_date')}
              </button>
            </TableHead>
            <TableHead className='w-[130px]'>
              <button
                className='flex items-center hover:text-foreground transition-colors'
                onClick={() => handleSortClick('discontinue_date')}
              >
                Discontinue
                {getSortIcon('discontinue_date')}
              </button>
            </TableHead>
            <TableHead>
              <button
                className='flex items-center hover:text-foreground transition-colors'
                onClick={() => handleSortClick('medication')}
              >
                Medication
                {getSortIcon('medication')}
              </button>
            </TableHead>
            {showMarColumn && (
              <TableHead className='w-[220px]'>Administration</TableHead>
            )}
            <TableHead className='w-[80px]'></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedMedications.map(med => (
            <MedicationTableRow key={med.id} {...rowProps(med)} />
          ))}
          {/* Imported lines sit after the coded rows rather than interleaved:
            Kinnser's own ordering is not recoverable from the portfolio
            contract, so any position among the coded rows would imply a
            sequence nobody reported. */}
          {importedLines.map(line => (
            <ImportedMedicationRow
              key={line.id}
              line={line}
              showMarColumn={showMarColumn}
              showSelectionColumn={Boolean(selection)}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default MedicationsTable;
